'use client';

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mail, MessageCircle, ExternalLink, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface SupportModalProps {
  children?: React.ReactNode;
  discordUrl?: string;
  supportEmail?: string;
}

export default function SupportModal({ 
  children, 
  discordUrl = "https://discord.gg/hBe7xpYM", 
  supportEmail = "support@flightdeck.to" 
}: SupportModalProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleEmailSupport = () => {
    const subject = encodeURIComponent('FlightDeck Support Request');
    const body = encodeURIComponent(`Hello FlightDeck Support Team,

I need assistance with:

[Please describe your issue here]

Browser: ${navigator.userAgent}
URL: ${window.location.href}
Time: ${new Date().toISOString()}

Thank you for your help!`);
    
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
    setIsOpen(false);
  };

  const handleDiscordSupport = () => {
    window.open(discordUrl, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} modal={true} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }}>
          {children || (
            <Button variant="outline" size="sm" className="flex items-center gap-2">
              <HelpCircle className="h-4 w-4" />
              Support
            </Button>
          )}
        </div>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md w-[90vw] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            {t('support.get_support')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('support.choose_option')}
          </p>

          <div className="space-y-3">
            {/* Email Support */}
            <Button
              onClick={handleEmailSupport}
              variant="outline"
              className="w-full justify-start h-auto p-3 min-h-[60px]"
            >
              <div className="flex items-center gap-3 w-full min-w-0">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900/20 p-2 flex-shrink-0">
                  <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <h3 className="font-medium truncate">{t('support.email_support')}</h3>
                  <p className="text-xs text-muted-foreground leading-tight">
                    {t('support.email_support_description')}
                  </p>
                </div>
                <ExternalLink className="h-4 w-4 opacity-50 flex-shrink-0" />
              </div>
            </Button>

            {/* Discord Support */}
            <Button
              onClick={handleDiscordSupport}
              variant="outline"
              className="w-full justify-start h-auto p-3 min-h-[60px]"
            >
              <div className="flex items-center gap-3 w-full min-w-0">
                <div className="rounded-full bg-indigo-100 dark:bg-indigo-900/20 p-2 flex-shrink-0">
                  <MessageCircle className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="text-left flex-1 min-w-0">
                  <h3 className="font-medium truncate">{t('support.discord_community')}</h3>
                  <p className="text-xs text-muted-foreground leading-tight">
                    {t('support.discord_community_description')}
                  </p>
                </div>
                <ExternalLink className="h-4 w-4 opacity-50 flex-shrink-0" />
              </div>
            </Button>
          </div>

          <div className="pt-2">
            <p className="text-xs text-muted-foreground text-center leading-relaxed">
                {t('support.urgent_issues')}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}